import { useState } from 'react';
import { ListChecks, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill } from '@/components/ui/status-pill';
import { SelectField } from '@/components/ui/select';
import { form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import {
  use_services,
  use_create_service,
  use_update_service,
  use_delete_service,
} from '@/features/vendor-service/hooks';
import type {
  CremationServiceType,
  LabSampleType,
  PetSpecies,
  VendorModuleType,
  VendorService,
} from '@/features/vendor-service/types';
import { use_vendor_profile } from '@/features/vendor-profile/hooks';
import { CREMATION_SERVICE_OPTIONS } from '@/features/cremation/types';
import { ChipMultiSelect } from '@/components/chip-multi-select';
import { VISIT_MODE_OPTIONS, type VisitMode } from '@/features/vendor-service/types';
import { LAB_SAMPLE_TYPE_OPTIONS } from '@/features/lab/types';
import { ImageGalleryEditor } from '@/components/image-gallery-editor';
import { ServiceThumb } from '@/components/service-thumb';
import { MODULE_LABEL } from '@/features/discovery/types';
import { StagedImageGallery, type StagedImage } from '@/components/staged-image-gallery';
import { upload_image } from '@/features/catalog-image/api';
import { useQueryClient } from '@tanstack/react-query';
import { use_grooming_packages } from '@/features/grooming-package/hooks';

const CREMATION_TYPE_LABEL: Record<CremationServiceType, string> = Object.fromEntries(
  CREMATION_SERVICE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<CremationServiceType, string>;

const LAB_SAMPLE_LABEL: Record<LabSampleType, string> = Object.fromEntries(
  LAB_SAMPLE_TYPE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<LabSampleType, string>;

type FormState = {
  module_type: VendorModuleType | '';
  name: string;
  description: string;
  price_inr: string;
  duration_minutes: string;
  cremation_service_type: CremationServiceType[];
  visit_modes: VisitMode[];
  /** Which pets this service is for. Empty means "everything this vendor accepts". */
  species: PetSpecies[];
  lab_sample_type: LabSampleType | '';
  lab_fasting_required: boolean;
  lab_turnaround_hours: string;
  is_active: boolean;
};

const EMPTY: FormState = {
  module_type: '',
  name: '',
  description: '',
  price_inr: '',
  duration_minutes: '',
  cremation_service_type: [],
  visit_modes: [],
  species: [],
  lab_sample_type: '',
  lab_fasting_required: false,
  lab_turnaround_hours: '',
  is_active: true,
};

export function VendorServicesPage() {
  const draft_q = use_draft();
  const services_q = use_services();
  /*
   * The species this business accepts, which is the menu a per-service list can be
   * narrowed from. The server refuses a service claiming an animal the vendor does
   * not accept, so offering anything wider here would only produce a 422.
   */
  const profile_q = use_vendor_profile();
  const vendor_species: PetSpecies[] = profile_q.data?.services_species ?? [];
  const create_m = use_create_service();
  const update_m = use_update_service();
  const delete_m = use_delete_service();

  const modules = draft_q.data?.module_types ?? [];
  // Grooming packages are VendorService rows too (module GROOMER, with a
  // GroomingPackage detail hung off them) — they're managed on the Packages
  // page, so exclude them here to avoid double-editing/deleting a tier's
  // underlying service (which would silently delete the package).
  const packages_q = use_grooming_packages({ enabled: modules.includes('GROOMER') });
  const package_service_ids = new Set((packages_q.data ?? []).map((p) => p.service_id));
  const services = (services_q.data ?? []).filter((s) => !package_service_ids.has(s.id));

  const qc = useQueryClient();
  const [editing, set_editing] = useState<null | { id?: string }>(null);
  const [form, set_form] = useState<FormState>(EMPTY);
  const [error, set_error] = useState<string | null>(null);
  // Photos chosen while creating (no service row exists yet) — uploaded on save.
  const [staged_photos, set_staged_photos] = useState<StagedImage[]>([]);

  function clear_staged() {
    staged_photos.forEach((p) => URL.revokeObjectURL(p.url));
    set_staged_photos([]);
  }

  function open_new() {
    set_error(null);
    clear_staged();
    set_form({ ...EMPTY, module_type: modules[0] ?? '' });
    set_editing({});
  }

  function open_edit(s: VendorService) {
    set_error(null);
    clear_staged();
    set_form({
      module_type: s.module_type,
      name: s.name,
      description: s.description ?? '',
      price_inr: s.price_inr,
      duration_minutes: s.duration_minutes != null ? String(s.duration_minutes) : '',
      cremation_service_type: s.cremation_service_type ?? [],
      visit_modes: s.visit_modes ?? [],
      species: s.species ?? [],
      lab_sample_type: s.lab_sample_type ?? '',
      lab_fasting_required: s.lab_fasting_required ?? false,
      lab_turnaround_hours: s.lab_turnaround_hours != null ? String(s.lab_turnaround_hours) : '',
      is_active: s.is_active,
    });
    set_editing({ id: s.id });
  }

  function close_form() {
    set_editing(null);
    set_error(null);
    clear_staged();
  }

  async function submit() {
    set_error(null);
    if (!form.module_type) return set_error('Pick a module for this service.');
    if (!form.name.trim()) return set_error('Name is required.');
    // `Number('')` is 0, so an untouched price field would silently publish a
    // paid service as free. Free is allowed, but it has to be deliberate.
    if (form.price_inr.trim() === '') {
      return set_error('Price is required — enter 0 if this service is free.');
    }
    const price = Number(form.price_inr);
    if (!Number.isFinite(price) || price < 0) return set_error('Enter a valid price.');
    if (price > 1_000_000) return set_error('Price cannot exceed ₹10,00,000.');
    const duration = form.duration_minutes.trim() === '' ? undefined : Number(form.duration_minutes);
    if (duration !== undefined && (!Number.isInteger(duration) || duration < 1)) {
      return set_error('Duration must be a whole number of minutes.');
    }

    const payload = {
      module_type: form.module_type,
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      price_inr: price,
      duration_minutes: duration,
      // TRAINER services are home-visit in v1 (the only supported mode).
      training_mode: form.module_type === 'TRAINER' ? ('HOME_VISIT' as const) : undefined,
      // CREMATION services carry the offerings they cover — several is normal, since
      // individual / witnessed / eco-friendly are choices about the same cremation.
      cremation_service_type: form.module_type === 'CREMATION' ? form.cremation_service_type : undefined,
      // LAB / CREMATION: how the service can be done (client change, 9 Oct 2026).
      visit_modes: form.module_type === 'LAB' || form.module_type === 'CREMATION' || form.module_type === 'GROOMER' ? form.visit_modes : undefined,
      /*
       * Which pets this service is for. Sent for every module, not just one: it is a
       * property of the service rather than of a vertical.
       *
       * An empty list is meaningful and deliberate - the server resolves it to the
       * vendor's own species and stores THAT, so the saved value is always explicit
       * and no reader has to interpret an empty array.
       */
      species: form.species,
      // LAB tests carry the sample type / fasting / turnaround (all nullable — optional per test).
      lab_sample_type: form.module_type === 'LAB' ? form.lab_sample_type || null : undefined,
      lab_fasting_required: form.module_type === 'LAB' ? form.lab_fasting_required : undefined,
      lab_turnaround_hours:
        form.module_type === 'LAB'
          ? form.lab_turnaround_hours.trim() === ''
            ? null
            : Number(form.lab_turnaround_hours)
          : undefined,
      is_active: form.is_active,
    };

    try {
      if (editing?.id) {
        await update_m.mutateAsync({ id: editing.id, input: payload });
      } else {
        const created = await create_m.mutateAsync(payload);
        // Upload any photos staged during creation, in the order chosen.
        if (staged_photos.length > 0) {
          for (const p of staged_photos) {
            await upload_image('VENDOR_SERVICE', created.id, p.file);
          }
          qc.invalidateQueries({ queryKey: ['catalog-images', 'VENDOR_SERVICE', created.id] });
        }
      }
      close_form();
    } catch (err) {
      set_error(form_error_message(err, 'Could not save the service.'));
    }
  }

  async function toggle_active(s: VendorService) {
    try {
      await update_m.mutateAsync({ id: s.id, input: { is_active: !s.is_active } });
    } catch {
      /* surfaced via list refetch; ignore inline */
    }
  }

  async function remove(s: VendorService) {
    if (!window.confirm(`Delete “${s.name}”? This cannot be undone.`)) return;
    await delete_m.mutateAsync(s.id);
  }

  const saving = create_m.isPending || update_m.isPending;

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Services"
        description="Priced offerings customers can book."
        action={
          !editing && (
            <Button
              onClick={open_new}
              variant="gradient"
              size="sm"
              data-testid="add-service"
              disabled={modules.length === 0}
            >
              <Plus className="h-4 w-4" /> Add service
            </Button>
          )
        }
      />

      {modules.length === 0 && !draft_q.isLoading && (
        <div className="rounded-input border-2 border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          Finish onboarding (pick at least one module) before adding services.
        </div>
      )}

      {/* Create / edit form */}
      {editing && (
        <Card data-testid="service-form">
          <div className="flex items-center justify-between border-b-2 border-brand-outline-variant/15 px-5 py-3.5">
            <h2 className="font-heading text-base font-bold">
              {editing.id ? 'Edit service' : 'New service'}
            </h2>
            <Button variant="ghost" size="icon" onClick={close_form} aria-label="Close" data-testid="svc-cancel">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="svc-module">Module</Label>
                <SelectField
                  id="svc-module"
                  data-testid="svc-module"
                  value={form.module_type}
                  onValueChange={(v) => set_form((f) => ({ ...f, module_type: v as VendorModuleType }))}
                  options={modules.map((m) => ({ value: m, label: m }))}
                />
                {form.module_type === 'TRAINER' && (
                  <p className="text-xs text-muted-foreground" data-testid="svc-training-mode-hint">
                    Home visit — you travel to the customer. More modes (training centre, online) coming soon.
                  </p>
                )}
              </div>
              {/*
                Which pets this service is for.

                Options come from the vendor's own declared species: the server refuses
                a service that claims an animal the business does not accept, so
                offering it here would only produce a 422. When they have declared
                nothing there is nothing to narrow, and the row explains that instead
                of showing an empty picker.

                Picking more than one is what makes the customer app offer a pet-type
                chooser on the service - worth saying, because it is not obvious that
                this field changes how the listing behaves.
              */}
              {vendor_species.length > 0 ? (
                <ChipMultiSelect
                  label="Pet types"
                  hint={
                    form.species.length === 0
                      ? `leave empty for all (${vendor_species.join(', ')})`
                      : form.species.length === 1
                        ? 'one type — customers see no chooser'
                        : 'customers pick their pet type when booking'
                  }
                  testid="svc-species"
                  options={vendor_species.map((sp) => ({ value: sp, label: sp }))}
                  selected={form.species}
                  on_toggle={(v) =>
                    set_form((f) => ({
                      ...f,
                      species: f.species.includes(v)
                        ? f.species.filter((x) => x !== v)
                        : [...f.species, v],
                    }))
                  }
                  disabled={saving}
                />
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="svc-species-none">
                  Add the pet types you serve on your profile to set them per service.
                </p>
              )}
              {(form.module_type === 'LAB' || form.module_type === 'CREMATION' || form.module_type === 'GROOMER') && (
                <ChipMultiSelect
                  label="How customers can book it"
                  hint="pick every option you offer — customers choose one"
                  testid="svc-visit-modes"
                  options={VISIT_MODE_OPTIONS[form.module_type]}
                  selected={form.visit_modes}
                  on_toggle={(v) =>
                    set_form((f) => ({
                      ...f,
                      visit_modes: f.visit_modes.includes(v) ? f.visit_modes.filter((x) => x !== v) : [...f.visit_modes, v],
                    }))
                  }
                  disabled={saving}
                />
              )}
              {form.module_type === 'CREMATION' && (
                <ChipMultiSelect
                  label="Cremation type"
                  hint="optional — pick any this service covers"
                  testid="svc-cremation-type"
                  options={CREMATION_SERVICE_OPTIONS}
                  selected={form.cremation_service_type}
                  on_toggle={(v) =>
                    set_form((f) => ({
                      ...f,
                      cremation_service_type: f.cremation_service_type.includes(v)
                        ? f.cremation_service_type.filter((x) => x !== v)
                        : [...f.cremation_service_type, v],
                    }))
                  }
                  disabled={saving}
                />
              )}
              {form.module_type === 'LAB' && (
                <>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="svc-lab-sample" className="flex items-center gap-1">
                      Sample type{' '}
                      <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <SelectField
                      id="svc-lab-sample"
                      data-testid="svc-lab-sample-type"
                      value={form.lab_sample_type}
                      onValueChange={(v) =>
                        set_form((f) => ({ ...f, lab_sample_type: v as LabSampleType }))
                      }
                      placeholder="Choose a sample type"
                      options={LAB_SAMPLE_TYPE_OPTIONS}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="svc-lab-turnaround" className="flex items-center gap-1">
                      Turnaround (hours){' '}
                      <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <Input
                      id="svc-lab-turnaround"
                      data-testid="svc-lab-turnaround-hours"
                      type="number"
                      min={1}
                      value={form.lab_turnaround_hours}
                      onChange={(e) => set_form((f) => ({ ...f, lab_turnaround_hours: e.target.value }))}
                      placeholder="24"
                    />
                  </div>
                  <label
                    className="flex items-center gap-2 text-sm sm:col-span-2"
                    data-testid="svc-lab-fasting-label"
                  >
                    <input
                      type="checkbox"
                      data-testid="svc-lab-fasting"
                      checked={form.lab_fasting_required}
                      onChange={(e) => set_form((f) => ({ ...f, lab_fasting_required: e.target.checked }))}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    Fasting required before this test
                  </label>
                </>
              )}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="svc-name">Name</Label>
                <Input
                  id="svc-name"
                  data-testid="svc-name"
                  value={form.name}
                  onChange={(e) => set_form((f) => ({ ...f, name: e.target.value }))}
                  placeholder="General consultation"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="svc-price">Price (₹)</Label>
                <Input
                  id="svc-price"
                  data-testid="svc-price"
                  type="number"
                  min={0}
                  value={form.price_inr}
                  onChange={(e) => set_form((f) => ({ ...f, price_inr: e.target.value }))}
                  placeholder="500"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="svc-duration" className="flex items-center gap-1">
                  Duration{' '}
                  <span className="text-xs font-normal text-muted-foreground">(min, optional)</span>
                </Label>
                <Input
                  id="svc-duration"
                  data-testid="svc-duration"
                  type="number"
                  min={1}
                  value={form.duration_minutes}
                  onChange={(e) => set_form((f) => ({ ...f, duration_minutes: e.target.value }))}
                  placeholder="30"
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label htmlFor="svc-desc" className="flex items-center gap-1">
                  Description{' '}
                  <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                </Label>
                <textarea
                  id="svc-desc"
                  data-testid="svc-desc"
                  value={form.description}
                  onChange={(e) => set_form((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="rounded-input border-2 border-input bg-card px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15"
                  placeholder="What's included…"
                />
              </div>
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-input border-2 border-input bg-secondary/40 px-4 py-2.5 sm:col-span-2">
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">Active</span>
                  <span className="text-xs text-muted-foreground">Visible to customers and bookable</span>
                </span>
                <input
                  type="checkbox"
                  data-testid="svc-active"
                  checked={form.is_active}
                  onChange={(e) => set_form((f) => ({ ...f, is_active: e.target.checked }))}
                  className="h-5 w-5 shrink-0 accent-primary"
                />
              </label>
            </div>

            {editing.id ? (
              <ImageGalleryEditor owner_type="VENDOR_SERVICE" owner_id={editing.id} label="Service photos" />
            ) : (
              <StagedImageGallery files={staged_photos} on_change={set_staged_photos} label="Service photos" />
            )}

            {error && (
              <p className="text-sm text-destructive" data-testid="service-form-error" role="alert">
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button onClick={submit} variant="gradient" disabled={saving} data-testid="svc-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editing.id ? 'Save changes' : 'Create service'}
              </Button>
              <Button onClick={close_form} variant="ghost">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      {services_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : services_q.error ? (
        <ErrorState
          data-testid="services-error"
          title="Failed to load services"
          description={form_error_message(services_q.error, 'Please try again.')}
        />
      ) : services.length === 0 ? (
        <EmptyState
          data-testid="services-empty"
          icon={ListChecks}
          title="No services yet"
          description="Add your first offering so customers can book you."
          action={
            modules.length > 0 && (
              <Button onClick={open_new} variant="gradient">
                <Plus className="h-4 w-4" /> Add service
              </Button>
            )
          }
        />
      ) : (
        <Card>
          <ul className="divide-y-2 divide-brand-outline-variant/10" data-testid="services-list">
            {services.map((s) => (
              <li key={s.id} className="flex items-center gap-3 p-4" data-testid={`service-row-${s.id}`}>
                <ServiceThumb module_type={s.module_type} thumb_path={s.images?.[0]?.thumb_path} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{s.name}</span>
                    {/* The enum was being printed raw — a vendor reads "Grooming", not "GROOMER". */}
                    <StatusPill tone="neutral">{MODULE_LABEL[s.module_type] ?? s.module_type}</StatusPill>
                    {(s.cremation_service_type ?? []).map((t) => (
                      <StatusPill key={t} tone="neutral">
                        {CREMATION_TYPE_LABEL[t]}
                      </StatusPill>
                    ))}
                    {s.lab_sample_type && (
                      <StatusPill tone="neutral">{LAB_SAMPLE_LABEL[s.lab_sample_type]}</StatusPill>
                    )}
                    {s.lab_turnaround_hours != null && (
                      <StatusPill tone="neutral">{s.lab_turnaround_hours}h turnaround</StatusPill>
                    )}
                    {!s.is_active && <StatusPill tone="warning">Inactive</StatusPill>}
                  </div>
                  {s.description && (
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.description}</p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-heading text-base font-bold tabular-nums">₹{s.price_inr}</div>
                  {s.duration_minutes != null && (
                    <div className="text-xs text-muted-foreground">{s.duration_minutes} min</div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <Button variant="ghost" size="sm" onClick={() => toggle_active(s)} data-testid={`svc-toggle-${s.id}`}>
                    {s.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => open_edit(s)} data-testid={`svc-edit-${s.id}`} aria-label="Edit">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`svc-delete-${s.id}`} aria-label="Delete">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
